import { useEffect, useState } from "react";
//formik
import { Form, FormikProvider, useFormik } from "formik";
// mui components
import {
  Grid,
  FormControl,
  TextField,
  Typography,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  Card,
  CardContent,
  CardHeader,
  Box,
  Stack,
  Avatar,
  Chip,
  Paper,
  alpha,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Icon } from "@iconify/react";
import personFill from "@iconify/icons-eva/person-fill";
import emailFill from "@iconify/icons-eva/email-fill";
import phoneFill from "@iconify/icons-eva/phone-fill";
import mapFill from "@iconify/icons-eva/map-fill";
import checkmarkCircleFill from "@iconify/icons-eva/checkmark-circle-fill";
import { queryKeys } from "src/react-query/queryKeys";

// custom components, services and constants
import { BaseTable } from "src/components/BaseTable/BaseTable";
import { userProfileValidationSchema } from "src/util/validationSchemas";
import { Loader } from "src/components/Loader";
import Countries from "src/data/countries.json";
import { getUserProfile } from "src/react-query/endPoints";
import enums, { FEATURE } from "src/util/enums";
import {
  getUserProfileDefaultValues,
  roleColumns,
} from "./UserProfile.constant";
//actions
import { alertSlice } from "src/store/alert/alert.slice";

// hooks
import { useGetDetail, useMutate } from "src/hooks/useFetch.hook";
import { useAccess } from "src/hooks/useAccess.hook";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { FormHelperText } from "@mui/material";

function UserProfile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  // Allow users to edit their own profile regardless of user management permissions
  const isReadOnly = false;
  const { mutateAsync, isLoading } = useMutate("user");
  const { data, isFetching } = useGetDetail("profile", { id: 0 });
  const [isEditing, setIsEditing] = useState(true);
  
  const onSubmit = async (values) => {
    try {
      const {
        actions: { setAlert },
      } = alertSlice;
      const {
        id,
        zipcode,
        street,
        country,
        houseNumber,
        username,
        email,
        phone,
        lastName,
        firstName,
        city,
        language = "en",
      } = values;
      const profileReqBody = {
        username,
        email,
        phone,
        firstName,
        lastName,
        language,
        id,
        address: {
          zipcode,
          street,
          country,
          houseNumber,
          city,
          addressId: null,
        },
      };
      await mutateAsync(
        {
          url: getUserProfile(),
          method: "PUT",
          body: profileReqBody,
        },
        {
          onSuccess: () => {
            dispatch(
              setAlert({
                open: true,
                message: "Profile Updated Successfully",
                severity: enums.SEVERITY.SUCCESS,
              })
            );
            queryClient.invalidateQueries(queryKeys.user());
            // Keep profile in edit mode after successful save
          },
          onError: (error) => {
            dispatch(
              setAlert({
                open: true,
                message: error?.response?.data?.message ?? error?.message,
                severity: enums.SEVERITY.ERROR,
              })
            );
          },
        }
      );
    } catch (error) {
      console.error("Submission error:", error);
    }
  };

  const formik = useFormik({
    initialValues: getUserProfileDefaultValues,
    validationSchema: userProfileValidationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const { errors, touched, handleSubmit, getFieldProps, setFieldValue, isValid } =
    formik;
    

  useEffect(() => {
    if (!isFetching && data) {
      const profileData = {
        ...data,
        ...data.address,
      };
      delete profileData.company;
      delete profileData.role;
      for (let [k, v] of Object.entries(profileData)) {
        "role" !== k && setFieldValue(k, v);
      }
    }
  }, [data, isFetching]);

  const rowOrCellClickRedirect = ({ id }) => {
    navigate(`/team/${id}`);
  };
  if (isFetching) {
    return <Loader />;
  }

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={handleSubmit} autoComplete="off" id="profile" noValidate>
        <Box sx={{ position: 'relative' }}>
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                borderRadius: 2,
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #1976d2',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Updating profile...
                </Typography>
              </Box>
            </Box>
          )}
          <Grid container spacing={3}>
          {/* Profile Header Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: 3,
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: 'primary.main',
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                    }}
                  >
                    {getInitials(formik.values.firstName, formik.values.lastName)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      {formik.values.firstName} {formik.values.lastName}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      {formik.values.email}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip
                        icon={<Icon icon={personFill} width={16} height={16} />}
                        label={formik.values.username}
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        icon={<Icon icon={phoneFill} width={16} height={16} />}
                        label={formik.values.phone || 'No phone'}
                        variant="outlined"
                        size="small"
                      />
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Personal Information Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                title={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Icon icon={personFill} width={20} height={20} color={theme.palette.primary.main} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Personal Information
                    </Typography>
                  </Stack>
                }
                sx={{ pb: 1 }}
              />
              <CardContent>
                <Stack spacing={3}>
                  <TextField
                    label="Username"
                    disabled
                    fullWidth
                    size="small"
                    {...getFieldProps("username")}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <Icon icon={personFill} width={16} height={16} color="text.secondary" />
                        </Box>
                      ),
                    }}
                  />
                  <TextField
                    label="First Name"
                    fullWidth
                    size="small"
                    disabled={isReadOnly || isLoading}
                    {...getFieldProps("firstName")}
                    error={Boolean(touched.firstName && errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                    onFocus={() => {}}
                  />
                  <TextField
                    label="Last Name"
                    fullWidth
                    size="small"
                    disabled={isReadOnly || isLoading}
                    {...getFieldProps("lastName")}
                    error={Boolean(touched.lastName && errors.lastName)}
                    helperText={touched.lastName && errors.lastName}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Information Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                title={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Icon icon={emailFill} width={20} height={20} color={theme.palette.primary.main} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Contact Information
                    </Typography>
                  </Stack>
                }
                sx={{ pb: 1 }}
              />
              <CardContent>
                <Stack spacing={3}>
                  <TextField
                    label="Email Address"
                    fullWidth
                    size="small"
                    disabled={isReadOnly || isLoading}
                    {...getFieldProps("email")}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <Icon icon={emailFill} width={16} height={16} color="text.secondary" />
                        </Box>
                      ),
                    }}
                  />
                  <TextField
                    label="Phone Number"
                    fullWidth
                    size="small"
                    disabled={isReadOnly || isLoading}
                    {...getFieldProps("phone")}
                    error={Boolean(touched.phone && errors.phone)}
                    helperText={touched.phone && errors.phone}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <Icon icon={phoneFill} width={16} height={16} color="text.secondary" />
                        </Box>
                      ),
                    }}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Address Information Card */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Icon icon={mapFill} width={20} height={20} color={theme.palette.primary.main} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Address Information
                    </Typography>
                  </Stack>
                }
                sx={{ pb: 1 }}
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Street Address"
                      fullWidth
                      size="small"
                      disabled={isReadOnly || isLoading}
                      {...getFieldProps("street")}
                      error={Boolean(touched.street && errors.street)}
                      helperText={touched.street && errors.street}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="House Number"
                      fullWidth
                      size="small"
                      disabled={isReadOnly || isLoading}
                      {...getFieldProps("houseNumber")}
                      error={Boolean(touched.houseNumber && errors.houseNumber)}
                      helperText={touched.houseNumber && errors.houseNumber}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="City"
                      fullWidth
                      size="small"
                      disabled={isReadOnly || isLoading}
                      {...getFieldProps("city")}
                      error={Boolean(touched.city && errors.city)}
                      helperText={touched.city && errors.city}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Zip Code"
                      fullWidth
                      size="small"
                      disabled={isReadOnly || isLoading}
                      {...getFieldProps("zipcode")}
                      error={Boolean(touched.zipcode && errors.zipcode)}
                      helperText={touched.zipcode && errors.zipcode}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl
                      fullWidth
                      size="small"
                      error={Boolean(touched.country && errors.country)}
                    >
                      <InputLabel>Country</InputLabel>
                      <Select
                        disabled={isReadOnly || isLoading}
                        label="Country"
                        {...getFieldProps("country")}
                      >
                        {Countries.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.country && errors.country && (
                        <FormHelperText>{errors.country}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Action Buttons - Always Visible */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                borderRadius: 2,
              }}
            >
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isLoading}
                  disabled={isReadOnly}
                  startIcon={<Icon icon={checkmarkCircleFill} width={16} height={16} />}
                >
                  Save Changes
                </LoadingButton>
              </Stack>
            </Paper>
          </Grid>

          {/* User Roles Card */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Team Access & Roles
                  </Typography>
                }
                sx={{ pb: 1 }}
              />
              <CardContent>
                <BaseTable
                  rows={data?.role ?? []}
                  columns={roleColumns}
                  loading={isFetching}
                  getRowId={(row) => row.teamId}
                  rowOrCellClickRedirect={rowOrCellClickRedirect}
                />
              </CardContent>
            </Card>
          </Grid>
          </Grid>
        </Box>
      </Form>
    </FormikProvider>
  );
}

export default UserProfile;
